import React, { createContext, useState } from 'react';
import { UserRole } from '@leedpulse-monorepo/enums';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { Authentication } from '@leedpulse-monorepo/classes';
import { useEffect } from 'react';
import 'firebase/auth';
import {
  getAuth,
  IdTokenResult,
  signInWithEmailAndPassword,
  User,
} from 'firebase/auth';

export enum AuthenticationState {
  UNKNOWN,
  AUTHENTICATED,
  UNAUTHENTICATED,
}

const AuthenticationContext = createContext({
  authentication: null as Authentication | null,
  status: ContextStatus.IDLE,
  authenticationState: AuthenticationState.UNKNOWN,
  submitSignIn: (_: string, __: string) => new Promise<void>((resolve, reject) => null),
  submitSignOut: () => new Promise<void>((resolve, reject) => null),
});

export const AuthenticationProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [authentication, setAuthentication] = useState(
    null as Authentication | null
  );
  const [authenticationState, setAuthenticationState] = useState(
    AuthenticationState.UNKNOWN
  );
  const [status, setStatus] = useState(ContextStatus.IDLE);

  const updateAuthenticationState = async (user: User) => {
    const backofficeAllowedRoles = [
      UserRole.owner,
      UserRole.admin,
      UserRole.maintainer,
      UserRole.guest,
    ];

    const token = await user.getIdToken();
    const { uid, email } = user;
    const customClaims = await user
      .getIdTokenResult()
      .then((idTokenResult: IdTokenResult) => idTokenResult.claims);
    const role = customClaims.role as keyof typeof UserRole;

    if (!backofficeAllowedRoles.includes(role as UserRole)) {
      console.log('user not allowed: ', user);
      setStatus(ContextStatus.ERROR);
      await getAuth().signOut();
      return;
    }

    const newAuthentication = new Authentication(
      uid,
      email ?? 'unknown',
      UserRole[role],
      token
    );

    setAuthentication(newAuthentication);
    setAuthenticationState(AuthenticationState.AUTHENTICATED);
    setStatus(ContextStatus.SUCCESS);
  };

  useEffect(() => {
    const auth = getAuth();

    const unsubscribeAuthState = auth.onAuthStateChanged(async (user) => {
      if (user) {
        await updateAuthenticationState(user);
      } else {
        setAuthentication(null);
        setAuthenticationState(AuthenticationState.UNAUTHENTICATED);
        setStatus(ContextStatus.SUCCESS);
      }
    });

    const unsubscribeIdToken = auth.onIdTokenChanged(async (user) => {
      if (user) {
        await updateAuthenticationState(user);
      }
    });

    return () => {
      unsubscribeAuthState();
      unsubscribeIdToken();
    };
  }, []);


  const submitSignIn = async (email: string, password: string) => {
    setStatus(ContextStatus.LOADING);
    try {
      await signInWithEmailAndPassword(getAuth(), email, password);
    } catch (e) {
      console.log('error: ', e);
      setStatus(ContextStatus.ERROR);
    }
  };

  const submitSignOut = async () => {
    setStatus(ContextStatus.LOADING);
    try {
      await getAuth().signOut();
    } catch (e) {
      console.log('error: ', e);
      setStatus(ContextStatus.ERROR);
    }
  };

  return (
    <AuthenticationContext.Provider
      value={{
        authentication,
        status,
        authenticationState,
        submitSignIn,
        submitSignOut,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export default AuthenticationContext;
